import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase/supabaseClient";
import Button from "../components/atoms/Button/Button";
import {
  ErrorCode,
  errorMessageTranslator,
  isPasswordValid,
} from "./authHelpers";
import { AuthError } from "@supabase/supabase-js";
import { Checkbox } from "@mui/material";
import i18n from "../i18n/i18n";
import { useTranslation } from "react-i18next";

const RegisterForm: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    if (i18n.language !== navigator.language) {
      i18n.changeLanguage(navigator.language);
    }
  }, [t]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tncChecked, setTncChecked] = useState(false);

  const handleSignUp = async () => {
    if (!tncChecked) {
      setError(t("haveToAgreeToTNC"));
      return;
    }

    setError(null);

    if (password !== repeatPassword) {
      setError(t("passwordsDoNotMatch"));
      return;
    }
    if (!isPasswordValid(password)) {
      setError(t("passwordInvalid"));
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) throw error;
      setSuccess(true);
    } catch (error: unknown) {
      if (error instanceof AuthError) {
        const errorMessage = errorMessageTranslator(
          error.code as ErrorCode,
          "sign-up"
        );
        setError(errorMessage);
      } else {
        setError(t("registerError"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className="dui-auth-page" onSubmit={handleSignUp}>
      <div className="dui-auth-logo">
        <b>Doktor</b>UI
      </div>
      <div className="dui-auth-form">
        {success ? (
          <div className="dui-auth-confirmation">
            <div className="dui-auth-title">{t("accountCreationTitle")}</div>

            <div className="dui-auth-confirmation-description">
              {t("accountCreationConfirmation")}
            </div>
            <div className="dui-auth-form-footer">
              <a href="/login">{t("backToLogin")}</a>
            </div>
          </div>
        ) : (
          <div>
            <div className="dui-auth-title">{t("accountCreationTitle")}</div>
            {error && (
              <div className="dui-auth-error">
                <div className="dui-auth-error-text">{error}</div>
              </div>
            )}
            <div className="dui-auth-form-fields-wrapper">
              <div className="dui-auth-form-fields">
                <div className="dui-auth-field">
                  <div className="dui-auth-field-label">{t("email")}</div>
                  <input
                    className="dui-auth-field-input"
                    type="email"
                    placeholder={t("yourEmail")}
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <div className="dui-auth-field">
                  <div className="dui-auth-field-label">{t("password")}</div>
                  <input
                    className="dui-auth-field-input"
                    type="password"
                    placeholder={t("yourPassword")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <div className="dui-auth-field">
                  <div className="dui-auth-field-label">
                    {t("repeatPassword")}
                  </div>
                  <input
                    className="dui-auth-field-input"
                    type="password"
                    placeholder={t("yourPassword")}
                    value={repeatPassword}
                    onChange={(e) => setRepeatPassword(e.target.value)}
                  />
                </div>
                <div className="dui-auth-tnc">
                  <Checkbox
                    checked={tncChecked}
                    onChange={() => setTncChecked(!tncChecked)}
                  />
                  <div>
                    {t("tnc1")}{" "}
                    <a href="https://www.doktorui.com/static/DoktorUI_VOP.pdf">
                      {t("tnc2")}
                    </a>{" "}
                    {t("tnc3")}{" "}
                    <a href="https://www.doktorui.com/static/GDPR_doktorui.pdf">
                      {t("tnc4")}
                    </a>
                  </div>
                </div>
                <Button
                  className="dui-auth-button"
                  disabled={isLoading || !tncChecked}
                  onClick={handleSignUp}
                  htmlType="submit"
                >
                  {isLoading ? t("creatingAccount") : t("createAccount")}
                </Button>
              </div>
              <div className="dui-auth-form-footer">
                {t("alreadyHaveAccount")} <a href="/login">{t("loginHere")}</a>
              </div>
            </div>
          </div>
        )}
      </div>
    </form>
  );
};

export default RegisterForm;
